/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { HelperText } from '@components/ui/HelperText';
import { NotificationAPIContext } from '@components/ui/Notification/NotificationContext';
import { NotificationTypes } from '@components/ui/Notification/notification.utils';
import { isManagerVar, usernameVar, workshopVar } from '@graphql/cache';
import useLogin from '@graphql/hooks/useLogin';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, Icon, TextField } from '@mayoral/ui';
import { Box, FormControlLabel, Stack } from '@mui/material';
import { LoginMutationVariables } from '@sdk/core';
import { writeToCookies } from '@utils/cookies';
import { formatApolloError } from '@utils/errors';
import { useTranslation } from 'next-i18next';
import { FC, useContext } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import LoginContext from '../LoginContext';
import { loginSchema } from './LoginForm.schema';

import { AuthCookieExpiration, StorageTokens } from '@/lib/constants';

const LoginForm: FC = () => {
  const { isManager } = useContext(LoginContext);
  const { showNotification } = useContext(NotificationAPIContext);

  const { t } = useTranslation();
  const schema = loginSchema(isManager, t);
  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<LoginMutationVariables>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: schema.getDefaultFromShape() as LoginMutationVariables,
    resolver: yupResolver(schema),
  });
  const [login, { loading }] = useLogin({
    onError: (e) => {
      const formatedError = formatApolloError(e);
      setError('root.invalidCredentials', {
        type: 'server',
        message: formatedError.errorMessage,
      });

      showNotification({
        message: formatedError.errorMessage,
        type: NotificationTypes.ERROR,
        title: t('ERROR'),
      });
    },
  });

  const onSubmit: SubmitHandler<LoginMutationVariables> = async (data) => {
    clearErrors('root.invalidCredentials');

    // FIXME: This should be provided by the REST service.
    writeToCookies(StorageTokens.USERNAME, data.username, {
      expires: AuthCookieExpiration,
    });
    writeToCookies(StorageTokens.IS_MANAGER, isManager, {
      expires: AuthCookieExpiration,
    });
    writeToCookies(StorageTokens.SELECTED_WORKSHOP, +data.username);
    usernameVar(data.username);
    isManagerVar(isManager);
    workshopVar(+data.username);

    login({ variables: { ...data } });
  };

  const usernameError = !!errors.username || !!errors.root?.invalidCredentials;
  const passwordError = !!errors.password || !!errors.root?.invalidCredentials;

  return (
    <Stack direction="column">
      <Box
        component="form"
        id="login-form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box sx={{ typography: 'h3', fontWeight: 500, mb: 0.75 }}>{t('INICIAR_SESION')}</Box>
        <Box sx={{ typography: 'body1', color: 'grey.700', mb: 3 }}>{t('CREDENCIALES_ACCESO')}</Box>

        <Controller
          control={control}
          name="username"
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={usernameError}
              required
              name="username"
              label={t('USUARIO')}
              autoComplete="username"
              id="username"
              sx={{ mb: 1.5 }}
              variant="outlined"
              helperText={errors.username && <HelperText error={t(errors.username.message)} />}
              disabled={loading}
            />
          )}
        />

        <Controller
          control={control}
          name="password"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={passwordError}
              type="password"
              required
              name="password"
              label={t('CONTRASEÑA')}
              autoComplete="current-password"
              id="password"
              variant="outlined"
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />

        <FormControlLabel
          control={<Checkbox disabled={loading} />}
          label="Recordar datos"
          sx={{ mt: 0.5 }}
        />

        <Button
          type="submit"
          form="login-form"
          variant="contained"
          color="primary"
          size="large"
          loading={loading}
          endIcon={<Icon name="ArrowRightS" />}
          sx={{ mt: 1.5 }}
        >
          {t('CONTINUAR')}
        </Button>
      </Box>
    </Stack>
  );
};

export default LoginForm;
